import { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChatSidebar } from '@/components/chat/ChatSidebar';
import { ChatThread } from '@/components/chat/ChatThread';
import { MessageInput } from '@/components/chat/MessageInput';
import { AISuggestions } from '@/components/chat/AISuggestions';
import { AIHelper } from '@/components/chat/AIHelper';
import { useConversation, useConversations } from '@/lib/hooks/use-conversations';
import { useMessages } from '@/lib/hooks/use-messages';
import { useTypingIndicator } from '@/lib/hooks/use-typing-indicator';
import { useUserPreferences } from '@/lib/hooks/use-user-preferences';
import {
  useSubscribeToTyping,
  useSubscribeToConversation,
  useSubscribeToPresence,
  useSubscribeToNotifications,
} from '@/lib/websocket/realtime-chat';
import type { MessageInsert } from '@/lib/api/services/chat.service';

export default function Chat() {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationIdFromUrl = searchParams.get('conversationId');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(
    conversationIdFromUrl || null
  );
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [otherUserOnline, setOtherUserOnline] = useState(false);
  const messageInputRef = useRef<{ setValue: (value: string) => void } | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const [receivedMessageCount, setReceivedMessageCount] = useState(0); // Track messages received from other user

  const { conversation } = useConversation(selectedConversationId);
  const { sendMessage, markAsRead, markMultipleAsRead, messages } = useMessages(selectedConversationId);
  const { setTyping, stopTyping } = useTypingIndicator(selectedConversationId);
  const queryClient = useQueryClient();
  const { preferences: userPreferences } = useUserPreferences();

  // Track which messages we've already marked as read to prevent loops
  const markedAsReadRef = useRef<Set<string>>(new Set());

  // Mark messages as read when conversation is visible
  useEffect(() => {
    if (!selectedConversationId || !user?.id || !messages.length || document.hidden) return;

    // Get unread messages from other user that haven't been marked yet
    const unreadMessages = messages.filter(
      (m) => m.sender_id !== user.id && !m.is_read && !markedAsReadRef.current.has(m.id)
    );

    if (unreadMessages.length > 0) {
      // Mark all unread messages as read
      const messageIds = unreadMessages.map((m) => m.id);
      
      // Add to tracking set immediately to prevent duplicate calls
      messageIds.forEach((id) => markedAsReadRef.current.add(id));

      if (messageIds.length === 1) {
        markAsRead(messageIds[0])
          .then(() => {
            // Remove from tracking set after successful mark (in case message gets updated)
            setTimeout(() => {
              markedAsReadRef.current.delete(messageIds[0]);
            }, 5000);
          })
          .catch((error) => {
            // Remove from tracking set on error so we can retry
            messageIds.forEach((id) => markedAsReadRef.current.delete(id));
            console.error('Failed to mark message as read:', error);
          });
      } else if (messageIds.length > 1) {
        markMultipleAsRead(messageIds)
          .then(() => {
            // Remove from tracking set after successful mark
            setTimeout(() => {
              messageIds.forEach((id) => markedAsReadRef.current.delete(id));
            }, 5000);
          })
          .catch((error) => {
            // Remove from tracking set on error so we can retry
            messageIds.forEach((id) => markedAsReadRef.current.delete(id));
            console.error('Failed to mark messages as read:', error);
          });
      }
    }
  }, [selectedConversationId, user?.id, messages, markAsRead, markMultipleAsRead]);

  // Clear tracking set when conversation changes
  useEffect(() => {
    markedAsReadRef.current.clear();
  }, [selectedConversationId]);

  // Update URL when conversation is selected
  useEffect(() => {
    if (selectedConversationId) {
      setSearchParams({ conversationId: selectedConversationId }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [selectedConversationId, setSearchParams]);

  // Update selected conversation when URL param changes (only watch URL, not state)
  useEffect(() => {
    if (conversationIdFromUrl && conversationIdFromUrl !== selectedConversationId) {
      setSelectedConversationId(conversationIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationIdFromUrl]); // Only depend on URL changes, not selectedConversationId

  // Get all other user IDs from conversations for online status tracking
  const { conversations } = useConversations();
  const allOtherUserIds = conversations
    .flatMap(conv => conv.participants.filter(p => p.user_id !== user?.id).map(p => p.user_id))
    .filter((id, index, arr) => arr.indexOf(id) === index); // unique IDs
  
  // Track online status for all users in conversations
  const [userOnlineStatuses, setUserOnlineStatuses] = useState<Record<string, boolean>>({});
  
  // Subscribe to presence for all users in conversations
  useSubscribeToPresence(
    allOtherUserIds,
    (userId: string, isOnline: boolean) => {
      setUserOnlineStatuses(prev => ({ ...prev, [userId]: isOnline }));
    },
    user?.id
  );

  // Get other participant info
  const otherParticipant = conversation?.participants.find((p) => p.user_id !== user?.id);
  const otherUserId = otherParticipant?.user_id;
  // Use display_name from conversation participant (enriched by backend) instead of separate API call
  const otherUserName = otherParticipant?.display_name || null;

  // Reset online status and received message count when conversation changes
  useEffect(() => {
    setOtherUserOnline(false);
    lastMessageIdRef.current = null;
    setReceivedMessageCount(0);
  }, [selectedConversationId]);

  // Track new messages from other user and update received message count
  useEffect(() => {
    if (!selectedConversationId || !user?.id || !messages.length) {
      if (!selectedConversationId) {
        lastMessageIdRef.current = null;
        setReceivedMessageCount(0);
      }
      return;
    }

    // Count messages received from other user (not sent by current user)
    const receivedMessages = messages.filter((m) => m.sender_id !== user.id);
    const newReceivedCount = receivedMessages.length;
    
    // Only update if count changed (new message received)
    if (newReceivedCount !== receivedMessageCount) {
      setReceivedMessageCount(newReceivedCount);
    }

    // Get the most recent message
    const lastMessage = messages[messages.length - 1];
    
    // Initialize lastMessageIdRef if not set (first load of conversation)
    if (lastMessageIdRef.current === null) {
      lastMessageIdRef.current = lastMessage.id;
      return;
    }
    
    // Track last message ID for notification purposes
    if (lastMessage.id !== lastMessageIdRef.current) {
      lastMessageIdRef.current = lastMessage.id;
    }
  }, [messages, user?.id, selectedConversationId, receivedMessageCount]);

  // Helper function to show browser notifications
  const showNotification = (title: string, body: string, conversationId: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/apple-touch-icon.png',
        tag: conversationId, // Prevent duplicate notifications
      });
    }
  };

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);


  // Subscribe to typing indicators via WebSocket
  useSubscribeToTyping(
    selectedConversationId,
    user?.id || null,
    (userId: string, isTyping: boolean) => {
      if (userId === otherUserId) {
        setOtherUserTyping(isTyping);
      }
    },
    (error) => {
      console.error('Error in typing subscription:', error);
    }
  );

  // Subscribe to conversation updates
  useSubscribeToConversation(
    selectedConversationId,
    () => {
      // Invalidate conversation query to refresh data
      queryClient.invalidateQueries({ queryKey: ['conversation', selectedConversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    (error) => {
      console.error('Error in conversation subscription:', error);
    }
  );

  // Subscribe to presence (online/offline status) if we have other user ID
  useSubscribeToPresence(
    otherUserId ? [otherUserId] : [],
    (userId: string, isOnline: boolean) => {
      if (userId === otherUserId) {
        setOtherUserOnline(isOnline);
      }
    },
    user?.id
  );

  // Subscribe to notifications
  useSubscribeToNotifications((data) => {
    // Show notification if window is not focused
    if (document.hidden) {
      showNotification('New message', data.preview, data.conversation_id);
    }
    // Update unread counts via query invalidation
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  });

  // Handle typing indicator
  const handleTypingChange = (typing: boolean) => {
    if (selectedConversationId) {
      setTyping(typing);
    }
  };

  // Cleanup typing on unmount
  useEffect(() => {
    return () => {
      if (selectedConversationId) {
        stopTyping();
      }
    };
  }, [selectedConversationId, stopTyping]);

  // Handle sending messages (non-blocking)
  const handleSendMessage = (messageData: MessageInsert) => {
    if (!selectedConversationId) return;
    sendMessage(messageData);
  };

  // Handle AI suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    if (messageInputRef.current) {
      messageInputRef.current.setValue(suggestion);
    }
  };

  return (
    <div className="bg-slate-50">
      {/* Centered Container - matching app layout */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-[calc(100vh-82px)] flex flex-col bg-slate-50">
          {/* Main Chat Interface */}
          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* Sidebar */}
            <ChatSidebar
              selectedConversationId={selectedConversationId}
              onSelectConversation={setSelectedConversationId}
              otherUserOnlineStatuses={userOnlineStatuses}
            />

            {/* Chat Thread */}
            {selectedConversationId ? (
              <div className="flex-1 flex min-h-0 relative">
                <div className="flex-1 flex flex-col min-h-0">
                  <ChatThread
                    conversationId={selectedConversationId}
                    otherParticipant={
                      otherParticipant
                        ? {
                            user_id: otherParticipant.user_id,
                            name: otherUserName || (otherParticipant.role === 'genius' ? 'Freelancer' : 'Client'),
                            avatar: otherParticipant.profile_picture_url || undefined,
                            online: otherUserOnline,
                          }
                        : undefined
                    }
                    isTyping={otherUserTyping}
                  />
                  {/* AI Suggestions */}
                  <AISuggestions
                    conversationId={selectedConversationId}
                    onSuggestionClick={handleSuggestionClick}
                    messageCount={receivedMessageCount}
                    totalMessageCount={messages.length}
                  />
                  <MessageInput
                    ref={messageInputRef}
                    conversationId={selectedConversationId}
                    onSend={handleSendMessage}
                    onTypingChange={handleTypingChange}
                  />
                </div>
                {/* AI Helper - as sidebar (only show when open) */}
                {!userPreferences.helper_collapsed && (
                  <AIHelper 
                    conversationId={selectedConversationId}
                    receivedMessageCount={receivedMessageCount}
                  />
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="text-center">
                  <p className="text-lg text-slate-500">
                    Select a conversation to start chatting
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
